import React, { useState, useRef, lazy, Component } from 'react';
import { ChevronLeft, ChevronRight, BookOpenIcon, ClipboardListIcon, PuzzleIcon, BookmarkIcon, FileTextIcon, ArrowRightIcon, EyeIcon } from 'lucide-react';
type Item = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  desc: string;
  specs: string[];
  pages: string;
};
const items: Item[] = [{
  icon: BookOpenIcon,
  title: 'I 6 Volumi di ARTE PRONTA',
  desc: 'Percorsi artistici completi dalla 1ª alla 5ª classe + Guida Docente.',
  specs: ['6 PDF separati', 'Formato A4', 'Utilizzabili su LIM'],
  pages: '+200 pagine'
}, {
  icon: ClipboardListIcon,
  title: '100+ Attività Creative',
  desc: 'Laboratori artistici pratici e coinvolgenti per tutta la classe.',
  specs: ['Materiali semplici', 'Risultati spettacolari', 'Per tutta la classe'],
  pages: '100+ pagine'
}, {
  icon: PuzzleIcon,
  title: 'Guide Docente Complete',
  desc: 'Indicazioni chiare passo passo per ogni attività e laboratorio proposto.',
  specs: ['Obiettivi didattici', 'Procedura pronta', 'Suggerimenti inclusivi'],
  pages: '50+ pagine'
}, {
  icon: BookmarkIcon,
  title: 'Portfolio & Valutazione',
  desc: 'Rubriche, griglie e materiali già pronti per la valutazione descrittiva.',
  specs: ['Rubriche compilate', 'Portfolio artistico', 'Materiale editabile'],
  pages: '30+ pagine'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/xmbsvKGSZxrhaxfxMPPLNH/Captura-de-pantalla-2026-05-01-a-las-4.22.43_p.-m..webp',
  alt: 'Autoritratto allo Specchio',
  label: 'Autoritratto allo Specchio',
  chapter: 'Volume 1ª · Unità 1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/coYL52BFQyaiXHK2q6Qnjj/Captura-de-pantalla-2026-05-01-a-las-4.21.05_p.-m..webp',
  alt: 'Albero delle Stagioni',
  label: 'Albero delle Stagioni',
  chapter: 'Volume 2ª · Unità 3'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hEKDTN94M71gHGraVyzd3o/Captura-de-pantalla-2026-05-01-a-las-4.22.26_p.-m..webp',
  alt: 'Mondrian per Bambini',
  label: 'Mondrian per Bambini',
  chapter: 'Volume 3ª · Unità 2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/voTDyLibUm2dHDFFX5i9vQ/Captura-de-pantalla-2026-05-01-a-las-4.23.28_p.-m..webp',
  alt: 'Laboratorio Matisse',
  label: 'Laboratorio Matisse',
  chapter: 'Volume 4ª · Unità 4'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gPTrBvD4wN91oFAcb4gH4T/Captura-de-pantalla-2026-05-01-a-las-4.23.28_p.-m._1.webp',
  alt: 'Pop Art Creativa',
  label: 'Pop Art Creativa',
  chapter: 'Volume 5ª · Unità 5'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const center = container.scrollLeft + container.clientWidth / 2;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < container.children.length; i++) {
        const child = container.children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => activeIndex > 0 && scrollToIndex(activeIndex - 1);
  const scrollNext = () => activeIndex < carouselImages.length - 1 && scrollToIndex(activeIndex + 1);
  return <section className="w-full bg-bg-dark py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-79">
      {/* Background accents */}
      <div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-80" />
      <div className="absolute bottom-1/4 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-81" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-82">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-83">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-4" data-id="element-84">
            📦 Cosa c'è nella tua casella
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-85">
            350+ pagine di materiale
            <br className="hidden sm:block" data-id="element-86" />
            <span className="text-primary" data-id="element-87"> già pronto da usare.</span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-88">
            PDF + Word, stampabili in A4 o proiettabili sulla LIM.{' '}
            <span className="text-text-main font-bold" data-id="element-89">Da oggi stesso.</span>
          </p>
        </div>

        {/* Items grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5 mb-12 sm:mb-20" data-id="element-90">
          {items.map((item, i) => {
          const Icon = item.icon;
          return <article key={i} className="relative bg-bg-alt rounded-2xl border border-border-light p-5 sm:p-7 hover:border-primary/50 hover:-translate-y-0.5 transition-all duration-300 group" data-id="element-91">
                {/* Pages badge */}
                <div className="absolute top-4 right-4 sm:top-5 sm:right-5 inline-flex items-center gap-1 bg-bg-dark/60 border border-white/10 px-2.5 py-1 rounded-full" data-id="element-92">
                  <FileTextIcon className="w-3 h-3 text-primary" strokeWidth={2.5} data-id="element-93" />
                  <span className="text-text-main text-[10px] sm:text-xs font-black tracking-tight" data-id="element-94">
                    {item.pages}
                  </span>
                </div>

                {/* Icon */}
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-primary/20 group-hover:border-primary/60 transition-colors" data-id="element-95">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={2.25} data-id="element-96" />
                </div>

                <h3 className="text-lg sm:text-xl font-black text-text-main leading-tight mb-2 pr-20 sm:pr-24" data-id="element-97">
                  {item.title}
                </h3>
                <p className="text-text-sec text-sm sm:text-[15px] leading-relaxed mb-4" data-id="element-98">
                  {item.desc}
                </p>

                {/* Specs chips */}
                <div className="flex flex-wrap gap-1.5 pt-3 border-t border-border-light" data-id="element-99">
                  {item.specs.map((spec, j) => <span key={j} className="inline-flex items-center text-text-sec text-[11px] sm:text-xs font-bold bg-bg-dark/60 border border-white/10 px-2.5 py-1 rounded-md" data-id="element-100">
                      {spec}
                    </span>)}
                </div>
              </article>;
        })}
        </div>

        {/* Carousel header */}
        <div className="text-center mb-6 sm:mb-8" data-id="element-101">
          <span className="inline-flex items-center gap-2 text-secondary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-secondary/10 border border-secondary/20 px-4 py-1.5 rounded-full mb-3" data-id="element-102">
            <EyeIcon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-103" />
            Anteprima reale
          </span>
          <h3 className="text-2xl sm:text-3xl font-black text-text-main tracking-tight" data-id="element-104">
            Sfoglia 5 schede dal vero materiale
          </h3>
          <p className="text-text-sec text-sm sm:text-base mt-2" data-id="element-105">
            Ecco esattamente cosa stamperai per la tua classe.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative" data-id="element-106">
          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-107">
            {/* Navigation buttons */}
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Precedente" data-id="element-108">
              <ChevronLeft className="w-6 h-6" data-id="element-109" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Successivo" data-id="element-110">
              <ChevronRight className="w-6 h-6" data-id="element-111" />
            </button>

            {/* Slides */}
            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-180px)] pb-6 pt-4" data-id="element-112">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[360px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden flex flex-col shadow-[0_20px_50px_rgba(0,0,0,0.4)] transition-all duration-300 ${activeIndex === i ? 'scale-100 opacity-100 border-primary/50' : 'scale-95 opacity-50'}`} data-id="element-113">
                  <div className="p-2 sm:p-3 flex items-center justify-center bg-white/5" data-id="element-114">
                    <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[380px] sm:max-h-[420px] object-contain rounded-xl" data-id="element-115" />
                  </div>
                  <div className="px-4 py-3 border-t border-border-light bg-bg-dark/40" data-id="element-116">
                    <p className="text-[10px] sm:text-[11px] text-text-sec font-bold uppercase tracking-wider mb-0.5" data-id="element-117">
                      {img.chapter}
                    </p>
                    <p className="text-text-main font-black text-sm sm:text-base leading-tight" data-id="element-118">
                      {img.label}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Counter + Dots */}
          <div className="flex items-center justify-center gap-3 mt-2" data-id="element-119">
            <span className="text-text-sec text-xs font-bold tracking-wider tabular-nums" data-id="element-120">
              {String(activeIndex + 1).padStart(2, '0')}
              <span className="text-text-sec/40" data-id="element-121">
                {' '}
                / {String(carouselImages.length).padStart(2, '0')}
              </span>
            </span>
            <div className="flex items-center" data-id="element-122">
              {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="flex items-center justify-center min-w-[36px] min-h-[36px] p-2" aria-label={`Vai alla scheda ${i + 1}`} data-id="element-123">
                  <span className={`block h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-1.5'}`} data-id="element-124" />
                </button>)}
            </div>
          </div>

          <p className="text-center text-text-sec text-xs sm:text-sm mt-3 sm:hidden flex items-center justify-center gap-1.5" data-id="element-125">
            <ChevronLeft className="w-3.5 h-3.5 animate-pulse-composited" data-id="element-126" />
            Scorri per vedere di più
            <ChevronRight className="w-3.5 h-3.5 animate-pulse-composited" data-id="element-127" />
          </p>
        </div>

        {/* Bottom synthesis bar */}
        <div className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-border-light rounded-2xl overflow-hidden border border-border-light" data-id="element-128">
          {[{
          value: '350+',
          label: 'Pagine totali'
        }, {
          value: '6',
          label: 'Volumi PDF'
        }, {
          value: 'A4',
          label: 'Stampabile'
        }, {
          value: 'Word',
          label: 'Editabile'
        }].map((stat, i) => <div key={i} className="bg-bg-alt px-4 py-5 sm:py-6 text-center" data-id="element-129">
              <p className="text-primary font-black text-2xl sm:text-3xl tracking-tight leading-none" data-id="element-130">
                {stat.value}
              </p>
              <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1.5" data-id="element-131">
                {stat.label}
              </p>
            </div>)}
        </div>

        {/* Final CTA */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 bg-bg-alt border border-border-light rounded-2xl px-6 py-5" data-id="element-132">
          <p className="text-text-main text-base sm:text-lg font-semibold text-center sm:text-left leading-snug" data-id="element-133">
            ⚡ Tutto questo nel tuo computer in{' '}
            <span className="text-cta-green font-black" data-id="element-134">30 secondi</span>.
          </p>
          <a href="#pricing" style={{
          boxShadow: '0 0 20px rgba(57,255,20,0.55), 0 8px 24px rgba(57,255,20,0.35)'
        }} className="inline-flex items-center justify-center gap-2 font-black text-sm sm:text-base px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl border-2 border-white/30 whitespace-nowrap uppercase tracking-tight transition-transform hover:scale-[1.03] active:scale-[0.98] text-[#FFFFFF] bg-[#22C55E]" data-id="element-135">
            Voglio l'archivio completo
            <ArrowRightIcon className="w-4 h-4" strokeWidth={3} data-id="element-136" />
          </a>
        </div>
      </div>
    </section>;
}